import React, { useState } from 'react';
import { ChevronRight, ShieldCheck, Mail, Smartphone } from 'lucide-react';
import EmailVerification from './EmailVerification';
import PhoneVerification from './PhoneVerification';

const VerificationFlow = ({ userData, onComplete, onBack, onStepAdvance }) => {
    // Determine initial step based on user's current stepState
    const stepState = userData?.stepState || '';
    const initialStep = stepState === 'pending_verification_phone' ? 'phone' : 'email';
    const [currentStep, setCurrentStep] = useState(initialStep); // 'email' | 'phone' | 'complete'

    const userId = userData?.userId || userData?.daisUserId ||
        localStorage.getItem('daisies_user_id') || '';
    const userEmail = userData?.email || '';
    const userPhone = userData?.telefono || userData?.phone || '';

    const handleEmailVerified = () => {
        // Notificar al padre para que actualice el stepState localmente y oculte el banner
        if (onStepAdvance) onStepAdvance('pending_verification_phone');
        setCurrentStep('phone');
    };

    const handlePhoneVerified = () => {
        if (onStepAdvance) onStepAdvance('complete');
        setCurrentStep('complete');
        // Auto-navigate back after showing success
        setTimeout(() => {
            if (onComplete) onComplete();
        }, 2000);
    };

    // Step indicator
    const steps = [
        { id: 'email', label: 'Correo', icon: Mail },
        { id: 'phone', label: 'Teléfono', icon: Smartphone },
    ];

    const getStepStatus = (stepId) => {
        const stepOrder = ['email', 'phone', 'complete'];
        const currentIndex = stepOrder.indexOf(currentStep);
        const stepIndex = stepOrder.indexOf(stepId);

        if (stepIndex < currentIndex) return 'completed';
        if (stepIndex === currentIndex) return 'active';
        return 'pending';
    };

    return (
        <div className="min-h-screen bg-warm-canvas py-8 px-4 animate-fadeIn">
            <div className="max-w-lg mx-auto">
                {/* Back button */}
                <button
                    onClick={onBack}
                    className="mb-6 px-4 py-2 text-daisies hover:text-daisies-hover font-semibold flex items-center gap-2 transition-all text-sm"
                >
                    <ChevronRight className="rotate-180" size={16} />
                    Volver
                </button>

                {/* Step indicator */}
                <div className="flex items-center justify-center gap-4 mb-8">
                    {steps.map((step, index) => {
                        const status = getStepStatus(step.id);
                        const Icon = step.icon;
                        return (
                            <React.Fragment key={step.id}>
                                {index > 0 && (
                                    <div className={`h-0.5 w-12 transition-all ${status !== 'pending' ? 'bg-daisies' : 'bg-brand-100'
                                        }`} />
                                )}
                                <div className="flex flex-col items-center gap-1">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${status === 'completed'
                                        ? 'bg-mint-pill text-mint-deep'
                                        : status === 'active'
                                            ? 'bg-daisies text-white'
                                            : 'bg-brand-tint text-text-mute'
                                        }`}>
                                        {status === 'completed' ? (
                                            <ShieldCheck size={20} />
                                        ) : (
                                            <Icon size={20} />
                                        )}
                                    </div>
                                    <span className={`text-xs font-medium ${status === 'active' ? 'text-daisies' : status === 'completed' ? 'text-mint-deep' : 'text-text-mute'
                                        }`}>
                                        {step.label}
                                    </span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Main card */}
                <div className="bg-white rounded-2xl p-6 md:p-8">
                    {currentStep === 'email' && (
                        <EmailVerification
                            userId={userId}
                            userEmail={userEmail}
                            onVerified={handleEmailVerified}
                        />
                    )}

                    {currentStep === 'phone' && (
                        <PhoneVerification
                            userId={userId}
                            userPhone={userPhone}
                            onVerified={handlePhoneVerified}
                        />
                    )}

                    {currentStep === 'complete' && (
                        <div className="flex flex-col items-center justify-center py-12 animate-fadeIn">
                            <div className="w-24 h-24 bg-mint-pill rounded-full flex items-center justify-center mb-6">
                                <ShieldCheck className="text-mint-deep" size={56} />
                            </div>
                            <h3 className="text-2xl font-bold text-text-primary mb-2">¡Cuenta verificada!</h3>
                            <p className="text-text-body text-center mb-6">
                                Tu correo electrónico y número de teléfono han sido verificados exitosamente.
                            </p>
                            <button
                                onClick={onComplete}
                                className="px-8 py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all"
                            >
                                Continuar
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-text-mute">
                        ¿Necesitas ayuda? Contáctanos en{' '}
                        <a href="mailto:soporte@daisies.mx" className="text-daisies hover:underline">
                            soporte@daisies.mx
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VerificationFlow;
